import { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Breadcrumbs, Link as MuiLink, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Chip, Button,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import { getStandardControls, getControlChecks } from '../../../services/securityhubApi';
import { standardLabel } from './standards';
import { severityChipSx, stateChipSx } from './severity';
import FindingDetail from './FindingDetail';
import { formatStatusLabel } from '../../../utils/statusLabel';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalErrorState, PortalSectionHeader,
} from '../../../components/portal/PortalStates';
import {
  CK, FONT_BODY, statusChipSx, intentFromColor, tableHeadRowSx, headCellSx,
  rowSx, bodyCellSx, sectionCardSx, emptyTextSx,
} from '../portalStyles';

const STATUS_COLOR = { PASSED: 'success', FAILED: 'error', NO_DATA: 'default' };
const fmt = (t) => { const d = new Date(t); return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(); };

const tileLabelSx = {
  display: 'block',
  mb: 0.75,
  fontFamily: FONT_BODY,
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.08em',
  color: CK.muted,
};

export default function ControlDetail() {
  const { standardId, controlId } = useParams();
  const decodedStd = decodeURIComponent(standardId);
  const decodedCtrl = decodeURIComponent(controlId);
  const [control, setControl] = useState(null);
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openFindingId, setOpenFindingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, kRes] = await Promise.all([
        getStandardControls(decodedStd, {}),
        getControlChecks(decodedCtrl, { limit: 50 }),
      ]);
      setControl((cRes?.data || []).find((x) => x.securityControlId === decodedCtrl) || null);
      setChecks(kRes?.data?.findings || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [decodedStd, decodedCtrl]);
  useEffect(() => { load(); }, [load]);

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
      <Breadcrumbs sx={{ mb: 1.5 }}>
        <MuiLink component={RouterLink} to="/securityhub/cspm" underline="hover">Security Hub CSPM</MuiLink>
        <MuiLink component={RouterLink} to={`/securityhub/cspm/standards/${encodeURIComponent(decodedStd)}`} underline="hover">{standardLabel(decodedStd)}</MuiLink>
        <Typography color="text.primary">{decodedCtrl}</Typography>
      </Breadcrumbs>
      <PortalPageHeader
        icon={<SecurityIcon sx={{ fontSize: 24 }} />}
        title={control?.title || decodedCtrl}
        subtitle={control?.description}
        action={control?.remediationUrl ? (
          <Button
            variant="outlined"
            endIcon={<OpenInNewIcon />}
            href={control.remediationUrl}
            target="_blank"
            rel="noreferrer"
          >
            Remediation
          </Button>
        ) : null}
      />
      </motion.div>

      {error && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={load} />
        </motion.div>
      )}

      {loading ? (
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading control details…" />
          </Paper>
        </motion.div>
      ) : (
        <>
          <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
                <Typography variant="overline" sx={tileLabelSx}>Control status</Typography>
                <Chip size="small" label={formatStatusLabel(control?.controlStatus)} sx={statusChipSx(intentFromColor(STATUS_COLOR[control?.controlStatus] || 'default'))} />
              </Box>
              <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
                <Typography variant="overline" sx={tileLabelSx}>Severity</Typography>
                {control && <Chip size="small" label={control.severityRating} sx={severityChipSx(control.severityRating)} />}
              </Box>
              <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
                <Typography variant="overline" sx={tileLabelSx}>Failed checks</Typography>
                <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '0.875rem', color: CK.navy }}>
                  {control?.failedChecks} of {control?.totalChecks}
                </Typography>
              </Box>
            </Box>
          </Paper>
          </motion.div>

          <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalSectionHeader
              icon={<FactCheckOutlinedIcon sx={{ fontSize: 20 }} />}
              title={`Checks (${checks.length})`}
              subtitle="Individual resource evaluations behind this control"
              mb={2}
            />
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={tableHeadRowSx}>
                    <TableCell sx={headCellSx}>Compliance</TableCell>
                    <TableCell sx={headCellSx}>Workflow</TableCell>
                    <TableCell sx={headCellSx}>Account</TableCell>
                    <TableCell sx={headCellSx}>Region</TableCell>
                    <TableCell sx={headCellSx}>Resource</TableCell>
                    <TableCell sx={headCellSx}>Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {checks.map((f, i) => (
                    <TableRow key={f.findingId} hover sx={{ ...rowSx(i), cursor: 'pointer' }} onClick={() => setOpenFindingId(f.findingId)}>
                      <TableCell sx={bodyCellSx}>{f.complianceStatus && <Chip size="small" label={f.complianceStatus} sx={stateChipSx} />}</TableCell>
                      <TableCell sx={bodyCellSx}>{f.workflowStatus && <Chip size="small" label={f.workflowStatus} sx={stateChipSx} />}</TableCell>
                      <TableCell sx={bodyCellSx}>{f.accountId}</TableCell>
                      <TableCell sx={bodyCellSx}>{f.region}</TableCell>
                      <TableCell sx={bodyCellSx}>{f.resourceType}</TableCell>
                      <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>{fmt(f.lastObservedAt)}</TableCell>
                    </TableRow>
                  ))}
                  {checks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                        <Typography sx={emptyTextSx}>
                          No checks yet — this control has no findings for your enrolled accounts.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          </motion.div>
        </>
      )}

      <FindingDetail
        findingId={openFindingId}
        engine="cspm"
        open={!!openFindingId}
        onClose={() => setOpenFindingId(null)}
        onUpdated={() => load()}
      />
    </motion.div>
  );
}
